import styled from "@emotion/styled";
import { FilterValues, FilterProps, SupportedKmsTypeFilter, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { Link } from "@kaltura/ds-react-components";
import { translate, extractDefaultFilterValues, getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { FilterWrapper } from "./FilterWrapper";
import { Filter } from "./filter/Filter";
import { getDisabledFilters } from "./dependencyUtils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useMemo } from "react";
import { isEqual } from "lodash";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";

export interface VerticalFilterBarProps {
    filters: FilterProps<SupportedKmsTypeFilter>[];
    values: FilterValues;
    onChange: (values: FilterValues) => void;
    noClearAll?: boolean;
    customTopFilterComponents?: KmsTypeComponent[];
}

const StyledButton = styled(Link)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

/**
 * vertical filter bar - holds all the filters, and manage their state
 */
export function VerticalFilterBar(props: VerticalFilterBarProps) {
    const sendButtonAnalytics = useButtonAnalytics();
    const { filters: initialFilters = [], values = {}, onChange, noClearAll, customTopFilterComponents = [] } = props;
    const filters = getDisabledFilters(values, initialFilters);

    /**
     * handle a single filter value change
     *
     * we expect the filterValues to be relevant for a specific filter params
     * (filter main param, its group items params, free text items params)
     */
    const handleChange = (filterValues: FilterValues) => {
        // new values for filter
        let currentFilterValues: FilterValues = { ...values, ...filterValues };

        // update filter dependency by new values
        const disabledFilters = getDisabledFilters(currentFilterValues, initialFilters);

        // turn off values of disabled filters
        // (group item values are reset by the filters)
        disabledFilters.forEach((filter) => {
            if (filter.disabled) {
                currentFilterValues = { ...currentFilterValues, [filter.param]: null } as FilterValues;
            }
        });

        onChange(currentFilterValues);
    };

    /**
     * default filters values
     */
    const defaultValues = useMemo(() => {
        return extractDefaultFilterValues(initialFilters);
    }, [initialFilters]);

    /**
     * handle clear all filter values
     */
    const handleClearAll = () => {
        sendButtonAnalytics("Filters - Clear all filters", ButtonClickAnalyticsType.FILTER);
        // reset to default filters values
        onChange({ ...defaultValues, keyword: "" });
    };

    return (
        <>
            {!noClearAll && (
                <StyledButton
                    onClick={handleClearAll}
                    disabled={isEqual(defaultValues, values)}
                    className="kms-ds-filter-bar-clear-all-filters-button"
                    component={"button"}
                    size={"small"}
                >
                    {translate("Clear all filters")}
                </StyledButton>
            )}
            {customTopFilterComponents.map((topComponent, index) => {
                const { component, props } = topComponent;
                const Component = getComponentFromPath(component);
                return (
                    <ErrorBoundary key={index} fallback={<></>}>
                        <Component key={index} {...props} filterValues={values ?? {}} onChange={handleChange} />
                    </ErrorBoundary>
                );
            })}
            {filters
                .filter((filter) => !filter.hidden)
                .map((filter, index) => (
                    <FilterWrapper
                        title={filter.title}
                        param={filter.param}
                        disabled={filter.disabled}
                        key={index + filter.param}
                    >
                        <Filter {...filter} values={values ?? {}} onChange={handleChange} />
                    </FilterWrapper>
                ))}
        </>
    );
}

export default VerticalFilterBar;
